import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, jobsAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import WorkerOnboardingTutorial from '../components/WorkerOnboardingTutorial';
import AdminViewBanner from '../components/AdminViewBanner';
import BookingPromoBanner from '../components/BookingPromoBanner';
import DashboardThemeToggle from '../components/DashboardThemeToggle';
import OnboardingChecklist from '../components/OnboardingChecklist';
import PullToRefresh from '../components/PullToRefresh';
import { StatsGridSkeleton } from '../components/Skeletons';
import WorkerTodayChecklists from '../components/worker/WorkerTodayChecklists';
import WorkerShoppingLists from '../components/worker/WorkerShoppingLists';
import { WorkerScheduleCard } from '../components/worker/WorkerScheduleCard';
import { WorkerTodayStrip } from '../components/worker/WorkerTodayStrip';
import { AISchedulerPromo } from '../components/AISchedulerPromo';
import RecentJobPhotos from '../components/RecentJobPhotos';
import { UsageBadge } from '../components/SubscriptionUI';
import MyPublicProfileBanner from '../components/contractor/MyPublicProfileBanner';
import { TelegramPromoCard } from '../components/TelegramPromoCard';
import LangToggle from '../components/LangToggle';
import { useWorkerLang } from '../contexts/WorkerLanguageContext';
import { 
  Briefcase, 
  CurrencyDollar, 
  Star,
  TrendingUp,
  MapPin,
  Clock,
  FileText,
  MagnifyingGlass,
  Bell,
  Gear,
  Heart,
  ShieldCheck,
  Question,
  CalendarBlank,
  DownloadSimple,
  ArrowRight,
  SpinnerGap,
  Camera,
  ClipboardText,
  User,
  Image,
  Timer,
  Wallet,
  FolderOpen,
  CalendarCheck,
  Gift,
  Microphone,
  Translate
} from '@phosphor-icons/react';

const WorkerDashboard = () => {
  const navigate = useNavigate();
  const { t } = useWorkerLang();
  const [userName, setUserName] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({
    applied_jobs: 0,
    pending_applications: 0,
    active_jobs: 0,
    month_earnings: 0,
    rating: 0,
    reviews_count: 0
  });
  const [recommendedJobs, setRecommendedJobs] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login?type=worker');
      return;
    }
    const storedType = localStorage.getItem('userType');
    if (storedType && storedType !== 'worker' && storedType !== 'admin') {
      navigate(storedType === 'contractor' ? '/contractor-dashboard' : storedType === 'homeowner' ? '/homeowner-dashboard' : '/login');
      return;
    }
    
    const storedName = localStorage.getItem('userName');
    if (storedName) {
      setUserName(storedName.split(' ')[0]);
    }
    
    const workerOnboardingCompleted = localStorage.getItem('constructhelp_worker_onboarding_completed');
    if (!workerOnboardingCompleted) {
      setTimeout(() => setShowOnboarding(true), 500);
    }
    
    fetchDashboardData();
  }, [navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashResponse, jobsResponse] = await Promise.all([
        dashboardAPI.getWorkerDashboard(),
        jobsAPI.getAll({ limit: 3 })
      ]);
      
      if (dashResponse.data.success) {
        setDashboardStats(dashResponse.data.stats);
      }
      
      if (jobsResponse.data.jobs) {
        setRecommendedJobs(jobsResponse.data.jobs.slice(0, 3));
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const restartTutorial = () => {
    localStorage.removeItem('constructhelp_worker_onboarding_completed');
    setShowOnboarding(true);
  };

  return (
    <PullToRefresh onRefresh={fetchDashboardData}>
    <div className="ios-dash min-h-screen bg-[#F4F7FC] dark:bg-neutral-900 transition-colors duration-300" data-testid="worker-dashboard">
      <AdminViewBanner role="worker" />
      {/* Header */}
      <div className="bg-[#2563EB] text-[#0F172A] py-6 border-b-4 border-[#0F172A] dark:border-[#64748B]">
        <div className="max-w-7xl mx-auto px-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-3xl md:text-4xl font-bold">{t('welcome_back')}{userName ? `, ${userName}` : ''}!</h1>
            <p className="text-[#94A3B8] text-lg mt-1">{t('find_subtitle')}</p>
          </div>
          <div className="flex-shrink-0 pt-1">
            <LangToggle />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <DashboardThemeToggle />
        <TelegramPromoCard />
        <BookingPromoBanner role="worker" />
        <AISchedulerPromo role="worker" />
        <OnboardingChecklist role="worker" />
        {/* Plan usage */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <UsageBadge counter="job_applications" label={t('apps_this_month')} />
        </div>

        {/* TODAY'S WORK — tasks, shopping lists, assigned schedule first */}
        <WorkerTodayStrip pendingApplications={dashboardStats.pending_applications || 0} />
        <div className="my-4"><WorkerTodayChecklists /></div>
        <div className="my-4"><WorkerShoppingLists /></div>
        <WorkerScheduleCard />

        {/* Primary Actions */}
        <div className="flex flex-wrap gap-3 mb-6">
          <button 
            onClick={() => navigate('/browse-jobs')}
            className="flex items-center gap-2 h-14 px-6 bg-[#2563EB] text-[#0F172A] font-bold text-lg rounded-xl border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all"
            data-testid="find-jobs-btn"
          >
            <MagnifyingGlass size={24} weight="bold" />
            {t('find_jobs')}
          </button>
          <button 
            onClick={() => navigate('/my-applications')}
            className="flex items-center gap-2 h-14 px-6 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white font-bold text-lg rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all"
            data-testid="my-applications-btn"
          >
            <ClipboardText size={24} weight="bold" />
            {t('my_applications')}
          </button>
          <button 
            onClick={() => navigate('/messages')}
            className="flex items-center gap-2 h-14 px-6 bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white font-bold text-lg rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all"
            data-testid="messages-btn"
          >
            <Bell size={24} weight="bold" />
            {t('messages')}
          </button>
          <button 
            onClick={() => navigate('/time-clock')}
            className="flex items-center gap-2 h-14 px-6 bg-[#D1FAE5] text-[#0F172A] font-bold text-lg rounded-xl border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all"
            data-testid="time-clock-btn"
          >
            <Timer size={24} weight="bold" />
            {t('time_clock')}
          </button>
          <button 
            onClick={() => navigate('/wallet')}
            className="flex items-center gap-2 h-14 px-6 bg-[#D1FAE5] text-[#0F172A] font-bold text-lg rounded-xl border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all"
            data-testid="wallet-btn"
          >
            <Wallet size={24} weight="bold" />
            {t('my_wallet')}
          </button>
        </div>

        {/* Tool Groups */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Work & Jobs */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-4 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB]">
            <h3 className="text-xs font-black uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">{t('group_work_jobs')}</h3>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => navigate('/saved-jobs')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 font-bold rounded-lg border-2 border-red-200 dark:border-red-800 hover:opacity-80 transition-all" data-testid="saved-jobs-btn"><Heart size={16} weight="bold" /> {t('saved_jobs')}</button>
              <button onClick={() => navigate('/browse-jobs?view=map')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold rounded-lg border-2 border-neutral-300 dark:border-neutral-600 hover:opacity-80 transition-all" data-testid="map-view-btn"><MapPin size={16} weight="bold" /> {t('map_view')}</button>
              <button onClick={() => navigate('/worker-teams')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 font-bold rounded-lg border-2 border-purple-300 dark:border-purple-700 hover:opacity-80 transition-all" data-testid="worker-teams-btn"><User size={16} weight="bold" /> {t('my_teams')}</button>
              <button onClick={() => navigate('/worker-control-panel?tab=portfolio')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-bold rounded-lg border-2 border-indigo-300 dark:border-indigo-700 hover:opacity-80 transition-all" data-testid="portfolio-btn"><Image size={16} weight="bold" /> {t('portfolio')}</button>
              <button onClick={() => navigate('/my-availability')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 font-bold rounded-lg border-2 border-emerald-300 dark:border-emerald-700 hover:opacity-80 transition-all" data-testid="my-availability-btn"><CalendarCheck size={16} weight="bold" /> {t('my_availability')}</button>
              <button onClick={() => navigate('/referrals')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 font-bold rounded-lg border-2 border-orange-300 dark:border-orange-700 hover:opacity-80 transition-all" data-testid="refer-earn-btn"><Gift size={16} weight="bold" /> {t('refer_earn')}</button>
            </div>
          </div>

          {/* Payments & Finance */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-4 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB]">
            <h3 className="text-xs font-black uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">{t('group_payments')}</h3>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => navigate('/escrow')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-emerald-100 dark:bg-emerald-900/50 text-emerald-700 dark:text-emerald-300 font-bold rounded-lg border-2 border-emerald-300 dark:border-emerald-700 hover:opacity-80 transition-all" data-testid="escrow-btn"><ShieldCheck size={16} weight="bold" /> {t('escrow')}</button>
              <button onClick={() => navigate('/invoices')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-lime-100 dark:bg-lime-900/50 text-lime-700 dark:text-lime-300 font-bold rounded-lg border-2 border-lime-300 dark:border-lime-700 hover:opacity-80 transition-all" data-testid="invoices-sec-btn"><FileText size={16} weight="bold" /> {t('invoices')}</button>
              <button onClick={() => navigate('/reports')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-pink-100 dark:bg-pink-900/50 text-pink-700 dark:text-pink-300 font-bold rounded-lg border-2 border-pink-300 dark:border-pink-700 hover:opacity-80 transition-all" data-testid="reports-btn"><DownloadSimple size={16} weight="bold" /> {t('reports')}</button>
            </div>
          </div>

          {/* Schedule & Tools */}
          <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-4 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#2563EB]">
            <h3 className="text-xs font-black uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">{t('group_schedule')}</h3>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => navigate('/calendar')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-bold rounded-lg border-2 border-blue-300 dark:border-blue-700 hover:opacity-80 transition-all" data-testid="calendar-btn"><CalendarBlank size={16} weight="bold" /> {t('calendar')}</button>
              <button onClick={() => navigate('/timesheets')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-cyan-100 dark:bg-cyan-900/50 text-cyan-700 dark:text-cyan-300 font-bold rounded-lg border-2 border-cyan-300 dark:border-cyan-700 hover:opacity-80 transition-all" data-testid="timesheets-btn"><CalendarBlank size={16} weight="bold" /> {t('timesheets')}</button>
              <button onClick={() => navigate('/job-alerts')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300 font-bold rounded-lg border-2 border-amber-300 dark:border-amber-700 hover:opacity-80 transition-all" data-testid="job-alerts-btn"><Bell size={16} weight="bold" /> {t('job_alerts')}</button>
              <button onClick={() => navigate('/my-applications')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 font-bold rounded-lg border-2 border-indigo-300 dark:border-indigo-700 hover:opacity-80 transition-all" data-testid="documents-btn"><FolderOpen size={16} weight="bold" /> {t('documents')}</button>
              <button onClick={() => navigate('/worker-control-panel')} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold rounded-lg border-2 border-neutral-300 dark:border-neutral-600 hover:opacity-80 transition-all" data-testid="control-panel-btn"><Gear size={16} weight="bold" /> {t('settings')}</button>
              <button onClick={restartTutorial} className="flex items-center gap-1.5 h-10 px-3 text-sm bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 font-bold rounded-lg border-2 border-neutral-300 dark:border-neutral-600 hover:opacity-80 transition-all" data-testid="help-tutorial-btn"><Question size={16} weight="bold" /> {t('help')}</button>
            </div>
          </div>
        </div>

        {/* Compact Voice Translator row */}
        <button
          onClick={() => navigate('/translator')}
          className="group w-full flex items-center gap-3 text-left bg-[#D1FAE5] text-[#0F172A] rounded-xl border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-0.5 active:shadow-none transition-all px-4 py-3 mb-6"
          data-testid="hero-translator-btn"
        >
          <div className="flex-shrink-0 w-10 h-10 bg-black/15 rounded-lg flex items-center justify-center">
            <Microphone size={22} weight="fill" className="text-[#0F172A]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-black leading-tight">{t('vt_title')}</p>
            <p className="text-xs opacity-80 truncate flex items-center gap-1"><Translate size={11} weight="fill" /> {t('vt_pill')}</p>
          </div>
          <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform flex-shrink-0" />
        </button>

        {/* My Public Profile Banner */}
        <MyPublicProfileBanner userType="worker" />

        {/* Stats Grid */}
        {loading ? (
          <StatsGridSkeleton />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]">
              <FileText size={32} weight="bold" className="text-[#64748B] mb-3" />
              <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">{t('applied_jobs')}</p>
              <p className="text-3xl font-bold text-neutral-900 dark:text-white">{dashboardStats.applied_jobs}</p>
              <p className="text-sm text-[#64748B] mt-1 font-medium">{dashboardStats.pending_applications} {t('pending')}</p>
            </div>

            <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]">
              <Briefcase size={32} weight="bold" className="text-emerald-600 mb-3" />
              <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">{t('active_jobs')}</p>
              <p className="text-3xl font-bold text-neutral-900 dark:text-white">{dashboardStats.active_jobs}</p>
              <p className="text-sm text-emerald-600 mt-1 font-medium">{t('in_progress')}</p>
            </div>

            <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]">
              <CurrencyDollar size={32} weight="bold" className="text-emerald-600 mb-3" />
              <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">{t('earnings_label')}</p>
              <p className="text-3xl font-bold text-emerald-600">${dashboardStats.month_earnings?.toLocaleString() || 0}</p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 font-medium">{t('this_month')}</p>
            </div>

            <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-5 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]">
              <Star size={32} weight="fill" className="text-amber-500 mb-3" />
              <p className="text-sm font-bold text-neutral-500 dark:text-neutral-400">{t('rating_label')}</p>
              <p className="text-3xl font-bold text-neutral-900 dark:text-white">{dashboardStats.rating?.toFixed(1) || '0.0'}</p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-1 font-medium">{dashboardStats.reviews_count} {t('reviews')}</p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recommended Jobs */}
          <div className="lg:col-span-2">
            <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-neutral-900 dark:text-white">{t('recommended_jobs')}</h2>
                <button 
                  onClick={() => navigate('/browse-jobs')}
                  className="flex items-center gap-2 text-[#64748B] font-bold hover:underline"
                >
                  {t('view_all')} <ArrowRight size={20} weight="bold" />
                </button>
              </div>
              <div className="space-y-4">
                {recommendedJobs.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase size={48} weight="light" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-4" />
                    <p className="text-neutral-500 dark:text-neutral-400 mb-4">{t('no_jobs_available')}</p>
                    <button
                      onClick={() => navigate('/browse-jobs')}
                      className="h-12 px-6 bg-[#2563EB] text-[#0F172A] font-bold rounded-xl border-2 border-[#0F172A] shadow-[3px_3px_0px_#0F172A] hover:shadow-[4px_4px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all"
                    >
                      {t('browse_all_jobs')}
                    </button>
                  </div>
                ) : (
                  recommendedJobs.map((job) => (
                    <div 
                      key={job._id} 
                      onClick={() => navigate(`/job/${job._id}`)}
                      className="border-2 border-neutral-200 dark:border-neutral-700 rounded-xl p-4 hover:border-[#2563EB] transition-all cursor-pointer"
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1">
                          <h3 className="font-bold text-neutral-900 dark:text-white mb-1">{job.title}</h3>
                          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">{job.company_name || t('company')}</p>
                          <div className="flex flex-wrap gap-3 mb-3">
                            <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
                              <MapPin className="h-4 w-4 mr-1" weight="bold" />
                              {job.location}
                            </div>
                            <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
                              <CurrencyDollar className="h-4 w-4 mr-1" weight="bold" />
                              ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
                            </div>
                            <div className="flex items-center text-sm text-neutral-600 dark:text-neutral-400">
                              <Clock className="h-4 w-4 mr-1" weight="bold" />
                              {job.duration}
                            </div>
                          </div>
                        </div>
                      </div>
                      <button 
                        className="w-full h-12 bg-[#2563EB] text-[#0F172A] font-bold rounded-lg border-2 border-[#0F172A] shadow-[2px_2px_0px_#0F172A] hover:shadow-[3px_3px_0px_#0F172A] active:translate-y-1 active:shadow-none transition-all"
                      >
                        {t('view_details')}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Profile Strength */}
            <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]">
              <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-4">{t('profile_strength')}</h3>
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-neutral-600 dark:text-neutral-400">{t('completion')}</span>
                  <span className="font-bold text-neutral-900 dark:text-white">85%</span>
                </div>
                <div className="w-full bg-neutral-200 dark:bg-neutral-700 rounded-full h-3 border border-[#0F172A] dark:border-neutral-600">
                  <div className="bg-[#2563EB] h-full rounded-full" style={{ width: '85%' }}></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center text-sm">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2 border border-[#0F172A]"></div>
                  <span className="text-neutral-700 dark:text-neutral-300">{t('profile_photo_added')}</span>
                </div>
                <div className="flex items-center text-sm">
                  <div className="w-3 h-3 bg-emerald-500 rounded-full mr-2 border border-[#0F172A]"></div>
                  <span className="text-neutral-700 dark:text-neutral-300">{t('skills_listed')}</span>
                </div>
                <div className="flex items-center text-sm">
                  <div className="w-3 h-3 bg-amber-500 rounded-full mr-2 border border-[#0F172A]"></div>
                  <span className="text-neutral-700 dark:text-neutral-300">{t('add_portfolio_items')}</span>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="w-full mt-4 font-bold border-2 border-[#0F172A] dark:border-neutral-700 shadow-[2px_2px_0px_#0F172A] dark:shadow-[2px_2px_0px_#2563EB] active:translate-y-1 active:shadow-none transition-all"
              >
                {t('complete_profile')}
              </Button>
            </div>

            {/* Quick Links */}
            <div className="bg-white dark:bg-neutral-800 border-2 border-[#0F172A] dark:border-neutral-700 rounded-xl p-6 shadow-[4px_4px_0px_#0F172A] dark:shadow-[4px_4px_0px_#2563EB]">
              <h3 className="text-lg font-bold text-neutral-900 dark:text-white mb-4">{t('quick_links')}</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/my-applications')}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-700 hover:border-[#2563EB] transition-all text-left"
                >
                  <FileText size={20} weight="bold" className="text-[#64748B]" />
                  <span className="font-medium text-neutral-900 dark:text-white">{t('my_applications')}</span>
                </button>
                <button 
                  onClick={() => navigate('/saved-jobs')}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-700 hover:border-[#2563EB] transition-all text-left"
                >
                  <Heart size={20} weight="bold" className="text-red-500" />
                  <span className="font-medium text-neutral-900 dark:text-white">{t('saved_jobs')}</span>
                </button>
                <button 
                  onClick={() => navigate('/escrow')}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-700 hover:border-[#2563EB] transition-all text-left"
                >
                  <ShieldCheck size={20} weight="bold" className="text-emerald-600" />
                  <span className="font-medium text-neutral-900 dark:text-white">{t('escrow_dashboard')}</span>
                </button>
                <button 
                  onClick={() => navigate('/messages')}
                  className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-neutral-200 dark:border-neutral-700 hover:border-[#2563EB] transition-all text-left"
                >
                  <Bell size={20} weight="bold" className="text-amber-500" />
                  <span className="font-medium text-neutral-900 dark:text-white">{t('messages')}</span>
                </button>
              </div>
            </div>

            {/* Recent Job Photos */}
            <RecentJobPhotos userType="worker" />
          </div>
        </div>
      </div>

      {/* Worker Onboarding Tutorial */}
      <WorkerOnboardingTutorial
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        onComplete={() => setShowOnboarding(false)}
      />
    </div>
    </PullToRefresh>
  );
};

export default WorkerDashboard;
